import { createAsyncThunk } from "@reduxjs/toolkit"

import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import { errorSave } from "../../../redux/errors.slice"
import { fetchSubcontractorComplianceCount } from "../../../redux/notificationCounts/notification-counts.thunks.ts"
import { type RootState } from "../../../store.ts"
import { type BasePagination } from "../../../types/api.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import * as services from "../api/services"
import { type SubcontractorListItem } from "../types/types.ts"
import { type SubcontractorsFilters } from "./subcontractors.types.ts"

export const fetchSubcontractorsFilterOptions = createAsyncThunk<any, void>(
  "subcontractors/fetchFilterOptions",
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getSubcontractorsFilterOptions()

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data
  }
)

export const fetchSubcontractors = createAsyncThunk<
  BasePagination<SubcontractorListItem>,
  SubcontractorsFilters & { isMore?: boolean }
>(
  "subcontractors/fetchSubcontractors",
  // `isMore` tells the reducer whether to append; it is not a query parameter.
  async (
    { isMore: _isMore, ...params },
    { dispatch, getState, rejectWithValue }
  ) => {
    const activeBranchId = selectActiveBranchId(getState() as RootState)
    const response = await services.getSubcontractors({
      ...params,
      ...(activeBranchId ? { team: activeBranchId } : {})
    })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data as BasePagination<SubcontractorListItem>
  }
)

export const createInviteSubcontractor = createAsyncThunk<any, any>(
  "subcontractors/createInviteSubcontractor",
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.createInviteSubcontractor(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Subcontractor invited successfully.")

    return response.data
  }
)

export const createSubcontractor = createAsyncThunk<any, any>(
  "subcontractors/createSubcontractor",
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.createSubcontractor(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Subcontractor created successfully.")
    dispatch(fetchSubcontractorComplianceCount())

    return response.data
  }
)

export const updateSubcontractor = createAsyncThunk<any, any>(
  "subcontractors/updateSubcontractor",
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.updateSubcontractor(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Subcontractor updated successfully.")
    // A renewed (or newly dated) document changes the sidebar badge.
    dispatch(fetchSubcontractorComplianceCount())

    return response.data
  }
)

export const uploadSubcontractorComplianceDocument = createAsyncThunk<any, any>(
  "subcontractors/uploadSubcontractorComplianceDocument",
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.uploadSubcontractorComplianceDocument(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Document uploaded successfully.")
    // A new or renewed expiration date changes the sidebar badge.
    dispatch(fetchSubcontractorComplianceCount())

    return response.data
  }
)

export const deleteSubcontractor = createAsyncThunk<any, string>(
  "subcontractors/deleteSubcontractor",
  async (subcontractorId, { dispatch, rejectWithValue }) => {
    const response = await services.deleteSubcontractor(subcontractorId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Subcontractor deleted successfully.")

    return { ...response.data, id: subcontractorId }
  }
)

export const fetchSubcontractorDetails = createAsyncThunk<any, string>(
  "subcontractors/fetchSubcontractorDetails",
  async (subcontractorId, { dispatch, rejectWithValue }) => {
    const response = await services.getSubcontractorDetails(subcontractorId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }
    return response.data
  }
)

export const suspendSubcontractor = createAsyncThunk<any, string>(
  "subcontractors/suspendSubcontractor",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.suspendSubcontractor({ id })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Subcontractor suspended successfully.")

    return response.data
  }
)

export const archiveSubcontractor = createAsyncThunk<any, string>(
  "subcontractors/archiveSubcontractor",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.archiveSubcontractor(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Subcontractor archived.")

    return response.data
  }
)

export const restoreSubcontractor = createAsyncThunk<any, string>(
  "subcontractors/restoreSubcontractor",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.restoreSubcontractor(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Subcontractor restored.")

    return response.data
  }
)

export const activateSubcontractor = createAsyncThunk<any, string>(
  "subcontractors/activateSubcontractor",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.activateSubcontractor({ id })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Subcontractor activated successfully.")

    return response.data
  }
)

export const rateSubcontractor = createAsyncThunk<any, any>(
  "subcontractors/rateSubcontractor",
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.rateSubcontractor(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Subcontractor rated successfully.")

    return response.data
  }
)

export const resendInviteSubcontractor = createAsyncThunk<any, string>(
  "subcontractors/resendInviteSubcontractor",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.resendInviteSubcontractor({ id })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Invite resent successfully.")

    return response.data
  }
)

export const inviteNoAccessSubcontractor = createAsyncThunk<any, string>(
  "subcontractors/inviteNoAccessSubcontractor",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.inviteNoAccessSubcontractor({ id })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Invite sent successfully.")

    return response.data
  }
)
